import React, { createContext, useContext, useState, useCallback } from 'react';

import { API_URL } from '../utils/api';

const ReflectContext = createContext();

export const useReflect = () => {
  const context = useContext(ReflectContext);
  if (!context) {
    throw new Error('useReflect must be used within a ReflectProvider');
  }
  return context;
};

// Theme to mood color mapping
const THEME_COLORS = {
  self_discovery: { bg: 'from-violet-900/30 to-purple-900/20', accent: 'violet' },
  family: { bg: 'from-amber-900/30 to-orange-900/20', accent: 'amber' },
  dreams_goals: { bg: 'from-cyan-900/30 to-blue-900/20', accent: 'cyan' },
  fun_creative: { bg: 'from-lime-900/30 to-green-900/20', accent: 'lime' },
};

export const ReflectProvider = ({ children }) => {
  // Flow state
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionCode, setSessionCode] = useState(null);
  const [creatorLabel, setCreatorLabel] = useState('');
  
  // Program Configuration
  const [programId, setProgramId] = useState(null);
  const [programConfig, setProgramConfig] = useState(null);  // Loaded program config
  
  // Selections
  const [audienceMode, setAudienceMode] = useState('middle_school');
  const [theme, setTheme] = useState(null);  // Legacy theme slug
  const [themeId, setThemeId] = useState(null);  // New Assessment Theme ID
  const [themeName, setThemeName] = useState(null);  // Theme display name
  const [topic, setTopic] = useState(null);
  const [mood, setMood] = useState(null);  // Optional mood selection
  const [inputMethod, setInputMethod] = useState(null);
  const [outputType, setOutputType] = useState('lyrics');
  
  // Available themes from API
  const [availableThemes, setAvailableThemes] = useState([]);
  
  // Reflection data
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  // Generated content
  const [generatedLyrics, setGeneratedLyrics] = useState('');
  const [styleSummary, setStyleSummary] = useState(null);
  
  // Music panel
  const [selectedLoops, setSelectedLoops] = useState([]);
  const [suggestedLoops, setSuggestedLoops] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get theme colors (use category-based colors for new themes)
  const themeColors = theme ? THEME_COLORS[theme] : THEME_COLORS.self_discovery;
  
  // Validate program code
  const validateProgramCode = useCallback(async (code) => {
    if (!code || !code.trim()) {
      setProgramId(null);
      setProgramConfig(null);
      return { valid: true, program: null };
    }
    
    try {
      const response = await fetch(`${API_URL}/api/reflect/validate-program`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ program_code: code.trim() })
      });
      
      if (response.ok) {
        const program = await response.json();
        setProgramId(program.program_id);
        setProgramConfig(program);
        // Set default audience from program
        if (program.default_audience) {
          setAudienceMode(program.default_audience);
        }
        return { valid: true, program };
      } else {
        const err = await response.json();
        return { valid: false, error: err.detail || 'Invalid program code' };
      }
    } catch (err) {
      return { valid: false, error: 'Failed to validate program code' };
    }
  }, []);
  
  // Create new session
  const createSession = useCallback(async (label, pin) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/reflect/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creator_label: label,
          pin: pin,
          audience_mode: audienceMode,
          program_id: programId  // Include program association
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create session');
      }
      
      const data = await response.json();
      setSessionCode(data.session_code);
      setCreatorLabel(label);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [audienceMode]);
  
  // Recover session
  const recoverSession = useCallback(async (label, pin) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/reflect/sessions/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creator_label: label, pin: pin })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Session not found');
      }
      
      const data = await response.json();
      setSessionCode(data.session_code);
      setCreatorLabel(data.creator_label);
      setAudienceMode(data.audience_mode || 'middle_school');
      setTheme(data.theme);
      setTopic(data.topic);
      setInputMethod(data.input_method);
      setOutputType(data.output_type || 'lyrics');
      setCurrentStep(data.current_step || 1);
      setGeneratedLyrics(data.generated_lyrics || '');
      setStyleSummary(data.style_summary);
      
      // Restore responses
      if (data.responses) {
        const resMap = {};
        data.responses.forEach(r => {
          resMap[r.question_id] = r.answer;
        });
        setResponses(resMap);
      }
      
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Update session
  const updateSession = useCallback(async (updates) => {
    if (!sessionCode) return;
    
    try {
      await fetch(`${API_URL}/api/reflect/sessions/${sessionCode}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
    } catch (err) {
      console.error('Failed to update session:', err);
    }
  }, [sessionCode]);
  
  // Fetch available themes from the new Assessment Theme system
  const fetchAvailableThemes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reflect/themes`);
      if (response.ok) {
        const data = await response.json();
        setAvailableThemes(data);
      }
    } catch (err) {
      console.error('Failed to fetch themes:', err);
    }
  }, []);
  
  // Fetch questions - supports both legacy (theme+topic) and new (themeId) systems
  const fetchQuestions = useCallback(async () => {
    // New system: use themeId if available
    if (themeId) {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/reflect/themes/${themeId}/questions`);
        if (response.ok) {
          const data = await response.json();
          setQuestions(data.questions || []);
          setCurrentQuestionIndex(0);
        }
      } catch (err) {
        console.error('Failed to fetch questions by theme_id:', err);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Legacy system: use theme + topic
    if (!theme || !topic) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        audience: audienceMode,
        theme: theme,
        topic: topic
      });
      
      const response = await fetch(`${API_URL}/api/reflect/questions?${params}`);
      if (response.ok) {
        const data = await response.json();
        setQuestions(data);
        setCurrentQuestionIndex(0);
      }
    } catch (err) {
      console.error('Failed to fetch questions:', err);
    } finally {
      setLoading(false);
    }
  }, [audienceMode, theme, topic, themeId]);
  
  // Save response
  const saveResponse = useCallback(async (questionId, answer) => {
    if (!sessionCode) return;
    
    // Update local state
    setResponses(prev => ({ ...prev, [questionId]: answer }));
    
    // Save to backend
    try {
      await fetch(`${API_URL}/api/reflect/sessions/${sessionCode}/responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: questionId, answer: answer })
      });
    } catch (err) {
      console.error('Failed to save response:', err);
    }
  }, [sessionCode]);
  
  // Generate lyrics
  const generateLyrics = useCallback(async (mood) => {
    if (!sessionCode) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ session_code: sessionCode });
      const response = await fetch(`${API_URL}/api/reflect/generate-lyrics?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          output_type: outputType,
          mood: mood
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedLyrics(data.lyrics);
        return data;
      }
    } catch (err) {
      console.error('Failed to generate lyrics:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionCode, outputType]);
  
  // Fetch suggested loops
  const fetchSuggestedLoops = useCallback(async (mood, energy) => {
    try {
      const params = new URLSearchParams();
      if (mood) params.append('mood', mood);
      if (energy) params.append('energy', energy);
      params.append('limit', '6');
      
      const response = await fetch(`${API_URL}/api/reflect/loops/suggested?${params}`);
      if (response.ok) {
        const data = await response.json();
        setSuggestedLoops(data);
      }
    } catch (err) {
      console.error('Failed to fetch suggested loops:', err);
    }
  }, []);
  
  // Shuffle vibe (random loop selection)
  const shuffleVibe = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reflect/loops/suggested?limit=6`);
      if (response.ok) {
        const data = await response.json();
        // Select 2-3 random loops from different groups
        const groups = {};
        data.forEach(loop => {
          const group = loop.instrument_group || 'Other';
          if (!groups[group]) groups[group] = [];
          groups[group].push(loop);
        });
        
        const selected = [];
        Object.values(groups).forEach(groupLoops => {
          if (groupLoops.length > 0 && selected.length < 3) {
            selected.push(groupLoops[Math.floor(Math.random() * groupLoops.length)]);
          }
        });
        
        setSelectedLoops(selected);
        setSuggestedLoops(data);
      }
    } catch (err) {
      console.error('Failed to shuffle vibe:', err);
    }
  }, []);
  
  // Reset flow
  const resetFlow = useCallback(() => {
    setCurrentStep(0);
    setSessionCode(null);
    setCreatorLabel('');
    setProgramId(null);
    setProgramConfig(null);
    setTheme(null);
    setThemeId(null);
    setThemeName(null);
    setTopic(null);
    setMood(null);
    setInputMethod(null);
    setResponses({});
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setGeneratedLyrics('');
    setStyleSummary(null);
    setSelectedLoops([]);
    setError(null);
  }, []);
  
  const value = {
    // Flow state
    currentStep,
    setCurrentStep,
    sessionCode,
    creatorLabel,
    
    // Program Configuration
    programId,
    programConfig,
    validateProgramCode,
    
    // Selections
    audienceMode,
    setAudienceMode,
    theme,
    setTheme,
    themeId,
    setThemeId,
    themeName,
    setThemeName,
    topic,
    setTopic,
    mood,
    setMood,
    inputMethod,
    setInputMethod,
    outputType,
    setOutputType,
    
    // Available themes from API
    availableThemes,
    
    // Questions & responses
    questions,
    responses,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    
    // Generated content
    generatedLyrics,
    setGeneratedLyrics,
    styleSummary,
    setStyleSummary,
    
    // Music
    selectedLoops,
    setSelectedLoops,
    suggestedLoops,
    
    // UI
    loading,
    error,
    themeColors,
    
    // Actions
    createSession,
    recoverSession,
    updateSession,
    fetchAvailableThemes,
    fetchQuestions,
    saveResponse,
    generateLyrics,
    fetchSuggestedLoops,
    shuffleVibe,
    resetFlow
  };
  
  return (
    <ReflectContext.Provider value={value}>
      {children}
    </ReflectContext.Provider>
  );
};

export default ReflectContext;
